/* Apparatus Driver/Operator skill-drill section.
 *
 * NOT auto-generated — unlike ./skillDrills.js, which is rebuilt from the VIERA/IFSTA
 * recruit drill list by .context/gen-skilldrills.js. These items are the OFC apparatus
 * driver/operator check sheet, which isn't part of that list, so they live here and are
 * merged into SkillDrills in index.js. Editing ./skillDrills.js instead would be silently
 * undone the next time it's regenerated. See #1579.
 *
 * Two properties this section relies on:
 *
 *  - `standalone: true` keeps it out of the ordered progression cascade. Exterior →
 *    Interior → Full Service imply each other by `order`; Driver/Operator is a parallel
 *    qualification, so completing it must not mark Exterior complete, and not having it
 *    must not gate Exterior. See isSectionComplete / isSectionEligible.
 *
 *  - Every provider writes the same `provincial.apparatusDriverOperator` cell (#2325).
 *    Provider switching therefore preserves the complete check sheet without a move.
 *
 * All chapters deliberately share ONE cell. Drills are stored at
 * `cell.practical.drills[<code>]` keyed by code, so the chapter split is presentational:
 * regrouping a drill later is a catalogue-only change with no data migration, as long as
 * the codes stay stable. That's why codes are `do-1`..`do-18` (the legacy
 * `driverOperatorSkill{N}` ordinals) rather than being derived from the chapter.
 */

const DRIVER_OPERATOR_CELL = {
    group: 'provincial',
    skillKey: 'apparatusDriverOperator'
};

// Chapters follow the conditional structure of the BC OFC Minimum Training Standards
// (Fourth Edition, July 2024 — p.15/p.20, and Policy 3.200 Annex pp.9-10, which is where
// the branch labels below come from verbatim): every large-apparatus driver needs EVD;
// the pump, aerial-device and mobile-water-supply branches apply only to drivers who
// operate that equipment. A branch nobody has signed off reads as not-applicable rather
// than incomplete.
//
// The items are OFC's own numbered sheets, one drill per sheet: `Apparatus Driver
// Operator — Interior Operations`, Skills Sheet Evaluations #1-#23, EMBC/OFC 2022.
// `do-N` is sheet #N, and each drill's `standard` is the JPR citation printed on that
// sheet. Sheets #19-#23 (the aerial branch) were absent from the legacy 18-item list;
// adding them costs nothing to members already signed off, because an untouched branch
// is not applicable and so never enters their denominator.
const DriverOperatorSkillDrills = {
    key: 'driverOperator',
    label: 'Driver/Operator',
    order: 10,
    // Not a rung on the Exterior → Interior → Full Service ladder.
    standalone: true,
    // Which mirrored document library backs this section's sheets and exam papers. VIERA
    // sections resolve theirs from the NFPA 1001 chapter number; this one is per drill
    // code, so the UI needs to know which library to ask. See common/ofcDriverOperatorSheets.js.
    documentSet: 'ofc-driver-operator',
    // Branch applicability is inferred from what's been signed off — see
    // applicableChapters in src/data/skillDrills.js.
    conditionalChapters: true,
    // Driver/Operator has no level roll-up cell of its own (there is no
    // `skills.driverOperator`); the qualification lives in the single matrix cell the
    // chapters write to, and that cell is what section sign-off completes.
    completionCell: DRIVER_OPERATOR_CELL,
    chapters: [
        {
            chapter: 1,
            label: 'Emergency Vehicle Driver',
            shortLabel: 'EVD',
            standard: 'NFPA 1002 (2017) §4.2, §4.3',
            ...DRIVER_OPERATOR_CELL,
            drills: [
                {code: 'do-1', label: '1', name: 'Cleaning and Waxing', standard: '4.2.1'},
                {code: 'do-2', label: '2', name: 'Perform a routine walk-around maintenance inspection', standard: '4.2.1, 4.2.2, 4.3.7'},
                {code: 'do-3', label: '3', name: 'Perform engine compartment inspection and routine preventive maintenance', standard: '4.2.1, 4.2.2'},
                {code: 'do-6', label: '6', name: 'Test Apparatus Road and Parking Brakes', standard: '4.2.1'},
                {code: 'do-7', label: '7', name: 'Drive a Fire Service Apparatus', standard: '4.3.1, 4.3.6'},
                {code: 'do-8', label: '8', name: 'Perform Various Driving Exercises', standard: '4.3.2, 4.3.3, 4.3.4, 4.3.5, 4.3.6'},
                {code: 'do-9', label: '9', name: 'Perform Various Road Test in a Fire Service Apparatus', standard: '4.3.1'}
            ]
        },
        {
            chapter: 2,
            label: 'Apparatus Equipped with a Fire Pump',
            shortLabel: 'Pump',
            standard: 'NFPA 1002 (2017) §5.1, §5.2',
            exam: 1,
            ...DRIVER_OPERATOR_CELL,
            drills: [
                {code: 'do-4', label: '4', name: 'Perform weekly inspections for apparatus equipped with a fire pump', standard: '4.2.1, 4.2.2, 5.1.2'},
                {code: 'do-5', label: '5', name: 'Perform a hard intake hose service test', standard: '4.2.2, 5.1.1'},
                {code: 'do-10', label: '10', name: 'Hose Intakes, Hydrant Outlets, Multiple Connections', standard: '5.2.1, 5.2.2, 5.2.4, 5.2.5, 5.2.7'},
                {code: 'do-11', label: '11', name: 'Dual Pumping and Tandem Pumping', standard: '5.2.1, 5.2.2, 5.2.4, 5.2.5, 5.2.7'},
                {code: 'do-12', label: '12', name: 'Engage and Disengage a PTO, Engage and Disengage a Pump', standard: '5.2.1, 5.2.2, 5.2.4, 5.2.5, 5.2.7'},
                {code: 'do-13', label: '13', name: 'Perform Pump Operations from a Water Tank, Transition from water tank to an external pressurized water supply, Operate from a pressurized water source', standard: '5.2.1, 5.2.4'},
                {code: 'do-14', label: '14', name: 'Draft from a Static Water Supply', standard: '5.2.3, 5.2.4, 5.2.5, 5.2.7'},
                {code: 'do-15', label: '15', name: 'Relay', standard: '5.2.2, 5.2.5'},
                {code: 'do-16', label: '16', name: 'Foam', standard: '5.2.6'},
                {code: 'do-17', label: '17', name: 'Fire Apparatus Testing', standard: '4.2.2, 5.1.2'}
            ]
        },
        {
            chapter: 3,
            label: 'Apparatus Equipped with an Aerial Device',
            shortLabel: 'Aerial',
            standard: 'NFPA 1002 (2017) §6.1, §6.2',
            exam: 2,
            ...DRIVER_OPERATOR_CELL,
            drills: [
                {code: 'do-19', label: '19', name: 'Visually Inspect a Fire Apparatus Aerial Device', standard: '4.2.1, 4.2.2, 6.1.1'},
                {code: 'do-20', label: '20', name: 'Deploy, lock, unlock, and raise Hydraulic Stabilizers', standard: '6.2.2'},
                {code: 'do-21', label: '21', name: 'Raise and Lower an Aerial Apparatus', standard: '6.2.3'},
                {code: 'do-22', label: '22', name: 'Lower an Aerial Apparatus using the Emergency Power Unit', standard: '6.2.4'},
                {code: 'do-23', label: '23', name: 'Elevated Master Stream', standard: '6.2.5'}
            ]
        },
        {
            chapter: 4,
            label: 'Mobile Water Supply Apparatus',
            shortLabel: 'Water supply',
            standard: 'NFPA 1002 (2017) §10.1, §10.2',
            exam: 3,
            ...DRIVER_OPERATOR_CELL,
            drills: [
                {code: 'do-18', label: '18', name: 'Mobile Water Supply, Water Shuttle Operation, Portable Water Tank Dump Site, Establish, Operate and Shut Down Mobile Portable Tank', standard: '5.2.3, 10.1.1, 10.2.1, 10.2.2, 10.2.3'}
            ]
        }
    ],
    // Every chapter writes to the SAME cell (see DRIVER_OPERATOR_CELL), so a per-branch
    // exam can't live at the cell's `theory.completedDate` the way a VIERA chapter exam
    // does — the three would overwrite each other. `examsShareCell` switches theory
    // storage to a per-exam map, `theory.exams[<exam>]`. See src/data/skillDrills.js
    // (examPassed) and api/src/routes/skillDrills.js (kind: 'theory').
    examsShareCell: true,
    // OFC publishes one written assessment per conditional branch and none for EVD, so
    // an exam is required only for a branch that applies to this member.
    exams: [
        {exam: 1, label: 'Pump — Written Assessment', chapters: [2]},
        {exam: 2, label: 'Aerial — Written Assessment', chapters: [3]},
        {exam: 3, label: 'Mobile Water Supply — Written Assessment', chapters: [4]}
    ]
};

module.exports = DriverOperatorSkillDrills;
